import { HumanReadable, parseSelector, serializeSelector } from '@fullstory/selectors';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import { SelectorChain } from '@fullstory/proto/fs/selectors';
import {
  ComputeElementStatsRequest,
  ComputeElementStatsResponse,
  MultiDatum,
} from '@fullstory/proto/fs/services/alexa';
import { int64, timestamp } from '@fullstory/proto/google/protobuf';
import { ElemBounds } from '../overlays/rectangles';
import {
  ClickAreaOverlayDisplayOptions,
  ClickMapDisplayOptions,
  ConversionOverlayDisplayOptions,
} from '@fullstory/page-insights-ui';
import type { ElementFilteredDetails } from './types';

// NB: Everything here is sent from the app into the renderer iframe and back,
// and therefore can only contain data suitable for postMessage. Only the
// properties of objects / instances of classes will survive the passage;
// functions will be undefined! (Unless you are using a proxy provided by the
// channel package, e.g., using CreateClient.)

// MatchingElement combines an element bounding box with statistics
// about how many clicks matched that element, the element's
// visibility information and selectors, etc.
export type MatchingElement = ElemBounds & { stats(): ElemStats };

export interface ElemStats {
  NamedElementId: string; // The id of the named element that matched, if any.
  Parsed: SelectorChain; // Full Selector. In parsed form.
  Selector: string; // Full Selector. Usable as a primary key.
  HumanReadableSelector: string; // A nicer form of the selector to display in the UI.
  PropertyValue: string | undefined; // If grouped by property, the property value for this named element / selector.
  Type: ComputeElementStatsRequest.StatsType; // all, dead, rage, error
  NumClicks: number; // Number of times this selector was clicked.
  NumUsers: number; // Number of users who clicked this element.
  NumElements: number; // Number of elements matched in the top-most (matching) Document.
  Stats: [Stat, Stat]; // Rendered human readable stats (deprecated: use NumClicks, NumUsers instead).
  Footnote: string; // A footnote rendered underneath the stats (deprecated: use NumElements instead).
  VisibilityInfo: VisibilityInfo; // Information about what the visibility of the element is inside the DOM.
}

// TODO(dutton): remove this in favor of NumClicks and NumUsers on ElemStats
export interface Stat {
  Name: string;
  Val: string;
  InfoText: string;
}

export interface VisibilityInfo {
  // Whether or not we queried and found any element at all associated with the selector.
  ExistsInDom: boolean;

  // Whether or not the element's bounding box is partially inside the viewport or not.
  IsInViewport: boolean;

  // Does this element have non-zero dimensions AND visibility. If so, it "could be clicked".
  CouldBeClicked: boolean;

  // Whether or not this element is on top or obscured by some other element.
  IsOnTop: boolean;

  // Whether or not this element is part of Shadow DOM
  ExistsInTheShadow: boolean;
}

export interface ActivityMaps {
  clear();

  enableMapInsights(enable: boolean);

  highlight(clicks: ClickFacet, displayOptions: ClickMapDisplayOptions);

  highlightClickArea(clicks: ClickFacet, displayOptions: ClickAreaOverlayDisplayOptions);

  highlightConversionZones(clickFacet: ClickFacet, displayOptions: ConversionOverlayDisplayOptions);

  // NB: selected option deprecated, if false, this method is a noop (selected = true deselects everything else)
  select(selector: string, propertyValue: string | undefined, selected?: boolean);

  setMapMode(enable: boolean);
}

export interface ActivityMapsHost {
  renderStats(
    clicks: ClickFacet,
    stats: ElemStats[],
    nativePlatform: NativePlatform,
    filteredElementDetails: ElementFilteredDetails[],
  );

  selectAt(idx: number);
}

//
// Client stubs for auto-proxying.
// TODO(jaime): Code gen this.
export class ActivityMapsClientStub implements ActivityMaps {
  clear() {}

  enableMapInsights(enable: boolean) {}

  highlight(clicks: ClickFacet, displayOptions: ClickMapDisplayOptions) {}

  highlightClickArea(clicks: ClickFacet, displayOptions: ClickAreaOverlayDisplayOptions) {}

  highlightConversionZones(
    clickFacet: ClickFacet,
    displayOptions: ConversionOverlayDisplayOptions,
  ) {}

  select(selector: string, propertyValue: string | undefined, selected: boolean = true) {}

  setMapMode() {}
}

export class ActivityMapsHostClientStub implements ActivityMapsHost {
  renderStats(
    clicks: ClickFacet,
    stats: ElemStats[],
    nativePlatform: NativePlatform,
    filteredLargeElements: ElementFilteredDetails[],
  ) {}

  selectAt(idx: number) {}
}

export type ClickType = '' | 'dead' | 'error' | 'rage';

export type NamedElementResolver = (id: string) => string;

// ClickFacet contains a list of click counts for a given page, aggregated by
// selector. The counts are for a single kind of click only, such as a normal
// click or a rage click.
export class ClickFacet {
  constructor(
    public type: ComputeElementStatsRequest.StatsType,
    public clicks: ClicksBySelector[],
    public totalClicks: number, // Total number of clicks found. This is NOT limited by the type of click.
    public totalUsers: number, // Total number of users found. This is NOT limited by the type of click.
    public matchingUsers: number, // Number of unique users matching the given stats type. (e.g., if a user rage clicked on multiple elements on a page, they are only counted as one user here).
    public canonicalPageUrl: string,
    public pageDefId: number,
    public pageViewEventStart: timestamp | null,
  ) {}

  static fromResponse(
    response: ComputeElementStatsResponse,
    resolver?: NamedElementResolver,
  ): ClickFacet {
    const selectorClicks =
      response.countResult?.counts
        ?.map(x => ClicksBySelector.fromMultiDatum(x))
        .filter(x => x !== undefined) ?? [];

    const namedElementClicks =
      response.countResult?.namedElementCounts
        ?.map(x => ClicksBySelector.fromMultiDatum(x, resolver))
        .filter(x => x !== undefined) ?? [];

    const byPropertyClicks =
      response.countResult?.elementPropertyCounts
        ?.map(x => {
          const maybeResolver =
            x.labelKind ===
            ComputeElementStatsResponse.MultiDatumElementProperty.LabelKind.LABEL_KIND_ELEMENT
              ? resolver
              : undefined;
          return ClicksBySelector.fromMultiDatum(x.datum, maybeResolver, x.propertyValue);
        })
        .filter(x => x !== undefined) ?? [];

    return new ClickFacet(
      response.statsType,
      namedElementClicks.concat(...selectorClicks).concat(...byPropertyClicks),
      int64.toNumber(response.countResult?.total),
      int64.toNumber(response.numMatchingIndividuals),
      int64.toNumber(response.countResult?.uniqueUsers),
      response.canonicalPageUrl,
      response.pageDefId,
      response.pageViewEventStart,
    );
  }
}

export interface FormattedSelector {
  readonly parsed: SelectorChain;
  readonly raw: string;

  // TODO(jaime): Inspect mode should have its own data type for this separate from click and activity maps.
  // Deprecate the distinction between "raw" and "human readable" selectors. These distinctions
  // go away with server side canonicalization.
  //
  // We should only have "raw" selectors that originate in the client - and only for inspect mode.
  // And then subsequently "Element identity selectors" that are passed from the server and used everywhere else.
  // Click and Activity maps should only ever operate with these when we have the right UX affordances for dealing
  // with the "raw" selectors coming from the client. With that we can build their "element identity" by highlighting
  // pieces of it.
  readonly humanReadable: string;
  readonly namedElementId: string;
}

export class ClicksBySelector {
  public readonly selector: FormattedSelector;

  constructor(
    parsedSelector: SelectorChain,
    rawSelector: string,
    humanReadable: string,
    public namedElementId: string,
    public propertyValue: string | undefined,
    public count: number,
    public users: number | undefined,
  ) {
    this.selector = {
      parsed: parsedSelector,
      raw: rawSelector,
      humanReadable,
      namedElementId,
    };
  }

  static fromMultiDatum(
    datum: MultiDatum,
    resolver?: NamedElementResolver,
    propertyValue?: string,
  ): ClicksBySelector | undefined {
    if (!datum.values || datum.values.length === 0 || !datum.label) {
      return undefined;
    }

    const rawSelectorStr = resolver ? resolver(datum.label) : datum.label;
    let namedElementId = '';
    let humanReadable = '';
    let parsed: SelectorChain = undefined;

    if (!resolver) {
      try {
        parsed = parseSelector(rawSelectorStr);
      } catch (e) {
        console.error(`Error parsing ${datum.label}:`, e);
      }

      if (!parsed) {
        return undefined;
      }

      humanReadable = serializeSelector(HumanReadable(parsed, 4));
    } else {
      // Found a named element. Don't try to parse the named element selector,
      // as it could be multiple selectors as one.
      namedElementId = datum.label;
      parsed = {} as SelectorChain;
    }

    return new ClicksBySelector(
      parsed,
      rawSelectorStr,
      humanReadable,
      namedElementId,
      propertyValue,
      /* count */ datum.values[0],
      /* users */ datum.values[1],
    );
  }
}
